import React, { useState, useEffect } from 'react';
import { getApiUrl, getServerName, getSystemID } from '../config/api';
import ImageModal from './ImageModal';

/** Raw base64 without data: prefix — pick MIME from magic bytes (stored photos are often PNG). */
function dataUrlFromRawBase64(raw) {
  const s = String(raw || '').trim().replace(/\s/g, '');
  if (!s) return null;
  if (s.startsWith('data:')) return s;
  if (s.startsWith('iVBORw0KGgo')) return `data:image/png;base64,${s}`;
  if (s.startsWith('R0lGOD')) return `data:image/gif;base64,${s}`;
  if (s.startsWith('/9j/')) return `data:image/jpeg;base64,${s}`;
  if (s.startsWith('UklGR')) return `data:image/webp;base64,${s}`;
  return `data:image/jpeg;base64,${s}`;
}

const SmartImage = ({ 
  base64Data, 
  photoURL, 
  alt = "Image", 
  className = "",
  style,
  fallbackSrc,
  placeholderType = 'default',
  clickable = true,
  modalTitle
}) => {
  const [loadedImageSrc, setLoadedImageSrc] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleImageClick = () => {
    if (clickable) {
      setShowModal(true);
    }
  };

  const getImageSrc = () => {
    if (base64Data && base64Data.trim() !== '') {
      return dataUrlFromRawBase64(base64Data);
    }
    if (loadedImageSrc) {
      return loadedImageSrc;
    }
    if (fallbackSrc) {
      return fallbackSrc;
    }
    return null;
  };

  const imageSrc = getImageSrc();

  // If we have base64 data, use it directly (strip newlines — they can break img src)
  if (base64Data && base64Data.trim() !== '') {
    const src = dataUrlFromRawBase64(base64Data);
    if (src) {
      return (
        <>
          <img 
            src={src}
            alt={alt}
            className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
            style={style}
            onClick={handleImageClick}
          />
          {clickable && imageSrc && (
            <ImageModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              imageSrc={imageSrc}
              imageAlt={alt}
              title={modalTitle || alt}
            />
          )}
        </>
      );
    }
  }

  // If photoURL is a data URL (base64), use it directly — no fetch needed
  const isDataUrl = photoURL && typeof photoURL === 'string' && photoURL.trim().startsWith('data:');
  if (isDataUrl) {
    const src = photoURL.trim();
    return (
      <>
        <img
          src={src}
          alt={alt}
          className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
          style={style}
          onClick={handleImageClick}
        />
        {clickable && (
          <ImageModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            imageSrc={src}
            imageAlt={alt}
            title={modalTitle || alt}
          />
        )}
      </>
    );
  }

  // If no base64 but we have a URL, try loading it via GetFileData in background
  useEffect(() => {
    if (!photoURL || photoURL.trim() === '' || loadedImageSrc) return;
    
    // Skip if photoURL looks invalid
    if (photoURL === 'null' || photoURL === 'undefined' || photoURL.length < 10) {
      return;
    }

    const loadImageFromURL = async () => {
      try {
        const response = await fetch(getApiUrl('GET_FILE_DATA'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ServerName: getServerName(),
            SystemID: getSystemID(),
            Purpose: "GETFILEDATA",
            SourceFilePath: photoURL,
            Source: "RXPROFILEWEB",
            DeviceType: "RXPROFILEWEB"
          })
        });

        if (!response.ok) {
          return; // Keep placeholder
        }

        // Parse the JSON response to get the base64 data
        const data = await response.json();
        
        // Check if we have the expected structure with DataB64
        if (data && data.DataB64 && typeof data.DataB64 === 'string') {
          const base64Data = data.DataB64;
          // Create the data URL with the base64 data
          const dataUrl = dataUrlFromRawBase64(base64Data) || `data:image/jpeg;base64,${base64Data}`;
          setLoadedImageSrc(dataUrl);
        } else {
          console.warn('GetFileData response does not contain DataB64 field:', data);
        }
        
      } catch (err) {
        // Keep placeholder on any error
        console.warn('Image load failed (keeping placeholder):', err);
      }
    };

    loadImageFromURL();
  }, [photoURL, loadedImageSrc]);

  // Show loaded image from URL if we got one
  if (loadedImageSrc) {
    return (
      <>
        <img 
          src={loadedImageSrc}
          alt={alt}
          className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
          style={style}
          onClick={handleImageClick}
        />
        {clickable && imageSrc && (
          <ImageModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            imageSrc={imageSrc}
            imageAlt={alt}
            title={modalTitle || alt}
          />
        )}
      </>
    );
  }

  // Show fallback image if provided
  if (fallbackSrc) {
    return (
      <>
        <img 
          src={fallbackSrc}
          alt={alt}
          className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
          style={style}
          onClick={handleImageClick}
        />
        {clickable && imageSrc && (
          <ImageModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            imageSrc={imageSrc}
            imageAlt={alt}
            title={modalTitle || alt}
          />
        )}
      </>
    );
  }

  // Always show plain grey placeholder (no loading/error states)
  const isDocument = placeholderType === 'document';
  const isFaceprint = placeholderType === 'faceprint';
  const baseClasses = className
    .split(' ')
    .filter(cls => !cls.startsWith('w-') && !cls.startsWith('h-') && cls !== 'object-cover')
    .join(' ');

  const documentStyles = isDocument ? {
    width: '192px',
    height: '256px',
    minHeight: '256px',
    maxHeight: '256px'
  } : isFaceprint ? {
    width: '64px',  // w-16
    height: '64px', // h-16
    minWidth: '64px',
    minHeight: '64px'
  } : {
    minHeight: '150px'
  };

  return (
    <div 
      className={`${baseClasses} flex items-center justify-center bg-gray-200 border border-gray-300 rounded-lg`}
      style={documentStyles}
    >
      {/* Empty grey box - no text or icons */}
    </div>
  );
};

export default SmartImage;